import type { ApiResponse } from './types';
import type { ApiClient } from './client';

export const importMethods = {
async analyzeModificationText(
      vehicleId: number,
      text: string,
      importType: string = 'text',
      options?: { useRulesOnly?: boolean; structuredImport?: boolean }
    ): Promise<any> {
      try {
        if (!this.isAuthenticated()) {
          throw new Error('Authentication required');
        }

        const response = await this.makeRequest<any>('/modifications_import_analyze.php', {
          method: 'POST',
          body: JSON.stringify({
            text,
            vehicle_id: vehicleId,
            import_type: importType,
            use_rules_only: options?.useRulesOnly === true,
            structured_import: options?.structuredImport === true,
          }),
        });

        if (response.success && response.data) {
          return response.data;
        } else {
          throw new Error(response.error || 'Failed to analyze text');
        }
      } catch (error: any) {
        console.error('Failed to analyze modification text:', error);
        throw new Error(error.message || 'Failed to analyze modification text');
      }
    },


    async importModificationsBatch(
      vehicleId: number,
      modifications: any[],
      importSessionId: string
    ): Promise<{ imported_count: number; errors: string[]; imported_modification_ids: number[] }> {
      const response = await this.makeRequest<{
        imported_count: number;
        errors: string[];
        imported_modification_ids: number[];
      }>('/modifications_import_batch.php', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          modifications,
          import_session_id: importSessionId,
        }),
      });

      if (!response.success) {
        throw new Error(response.error || response.message || 'Failed to import modifications batch');
      }

      return response.data || { imported_count: 0, errors: [], imported_modification_ids: [] };
    },


    async undoImportBatch(
      vehicleId: number,
      importSessionId: string
    ): Promise<{ deleted_count: number; deleted_ids: number[] }> {
      const response = await this.makeRequest<{
        deleted_count: number;
        deleted_ids: number[];
      }>('/modifications_import_undo.php', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          import_session_id: importSessionId,
        }),
      });

      if (!response.success) {
        throw new Error(response.error || response.message || 'Failed to undo import');
      }

      return response.data || { deleted_count: 0, deleted_ids: [] };
    },


    async getImportHistory(
      vehicleId: number,
      limit: number = 10
    ): Promise<Array<{
      session_token: string;
      status: string;
      imported_modifications: number;
      total_modifications: number;
      parse_source?: string | null;
      can_undo?: boolean;
      created_at?: string;
      completed_at?: string | null;
    }>> {
      const response = await this.makeRequest<{ sessions: any[] }>(
        `/modifications_import_history.php?vehicle_id=${vehicleId}&limit=${limit}`,
        { method: 'GET' }
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to load import history');
      }

      return response.data?.sessions ?? [];
    },


    async checkUsernameAvailability(username: string): Promise<boolean> {
      try {
        const response = await this.makeRequest('/auth/check_username.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username }),
        }) as ApiResponse<{ available: boolean }> & { available?: boolean };

        if (typeof response.available === 'boolean') {
          return response.available;
        }
        if (response.data && typeof response.data.available === 'boolean') {
          return response.data.available;
        }
        return false;
      } catch (err) {
        console.warn('checkUsernameAvailability failed:', err);
        throw err;
      }
    },

    async enhanceSession(
      vehicleId: number,
      action: 'create' | 'status' | 'discard' | 'complete' | 'start_over',
      sessionToken?: string
    ): Promise<{
      session?: any;
      active_session?: any | null;
      total_modifications?: number;
      batch_size?: number;
      resumed?: boolean;
      started_over?: boolean;
      message?: string;
    }> {
      const response = await this.makeRequest<any>('/modifications_enhance_session.php', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          action,
          ...(sessionToken ? { session_token: sessionToken } : {}),
        }),
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to manage enhancement session');
      }

      return response.data || {};
    },

    async enhanceModificationsAnalyze(
      vehicleId: number,
      options?: { limit?: number; offset?: number; session_token?: string }
    ): Promise<{
      vehicle_id: number;
      analyzed_count: number;
      suggestion_count: number;
      suggestions: any[];
      categories: string[];
      vendors: string[];
      offset: number;
      limit: number;
      returned: number;
      total: number;
      next_offset: number | null;
      previous_offset: number | null;
      has_more: boolean;
      session?: any;
      session_token?: string;
      range_label?: string;
    }> {
      const response = await this.makeRequest<any>('/modifications_enhance_analyze.php', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          limit: options?.limit ?? 50,
          offset: options?.offset ?? 0,
          ...(options?.session_token ? { session_token: options.session_token } : {}),
        }),
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to analyze modifications for enhancement');
      }

      return response.data;
    },

    async enhanceModificationsApply(
      vehicleId: number,
      updates: Array<{
        id: number;
        fields: Record<string, boolean>;
        values: Record<string, unknown>;
        edited_fields?: Record<string, boolean>;
      }>,
      options?: {
        session_token?: string;
        batch_offset?: number;
        stats?: Record<string, unknown>;
      }
    ): Promise<{
      applied_count: number;
      applied_ids: number[];
      errors: string[];
      apply_batch_id?: string;
      session?: any;
      can_undo?: boolean;
    }> {
      const response = await this.makeRequest<{
        applied_count: number;
        applied_ids: number[];
        errors: string[];
        apply_batch_id?: string;
        session?: any;
        can_undo?: boolean;
      }>('/modifications_enhance_apply.php', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          updates,
          ...(options?.session_token ? { session_token: options.session_token } : {}),
          ...(options?.batch_offset != null ? { batch_offset: options.batch_offset } : {}),
          ...(options?.stats ? { stats: options.stats } : {}),
        }),
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to apply enhancement updates');
      }

      return response.data || { applied_count: 0, applied_ids: [], errors: [] };
    },

    async enhanceModificationsUndo(
      vehicleId: number,
      options?: {
        session_token?: string;
        apply_batch_id?: string;
        skip_conflicts?: boolean;
        list_changes?: boolean;
      }
    ): Promise<{
      undone_count?: number;
      conflicts?: any[];
      apply_batch_id?: string;
      requires_review?: boolean;
      session?: any;
      changes?: any[];
    }> {
      const response = await this.makeRequest<any>('/modifications_enhance_undo.php', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          ...(options?.session_token ? { session_token: options.session_token } : {}),
          ...(options?.apply_batch_id ? { apply_batch_id: options.apply_batch_id } : {}),
          ...(options?.skip_conflicts ? { skip_conflicts: true } : {}),
          ...(options?.list_changes ? { list_changes: true } : {}),
        }),
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to undo enhancements');
      }

      return response.data || {};
    },

    async aiEditAnalyze(
      vehicleId: number,
      instruction: string,
      options?: {
        session_token?: string;
        selected_option_id?: string;
        selected_target_ids?: number[];
      }
    ): Promise<any> {
      const response = await this.makeRequest<any>('/modifications_ai_edit_analyze.php', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          instruction,
          ...(options?.session_token ? { session_token: options.session_token } : {}),
          ...(options?.selected_option_id ? { selected_option_id: options.selected_option_id } : {}),
          ...(options?.selected_target_ids ? { selected_target_ids: options.selected_target_ids } : {}),
        }),
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to analyze edit');
      }
      return response.data || {};
    },

    async aiEditApply(
      vehicleId: number,
      sessionToken: string,
      approvedOperationIds: string[],
      options?: {
        edited_values?: Record<string, Record<string, unknown>>;
        idempotency_key?: string;
        child_policy?: string;
      }
    ): Promise<any> {
      const response = await this.makeRequest<any>('/modifications_ai_edit_apply.php', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          session_token: sessionToken,
          approved_operation_ids: approvedOperationIds,
          ...(options?.edited_values ? { edited_values: options.edited_values } : {}),
          ...(options?.idempotency_key ? { idempotency_key: options.idempotency_key } : {}),
          ...(options?.child_policy ? { child_policy: options.child_policy } : {}),
        }),
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to apply edit');
      }
      return response.data || {};
    },

    async aiEditUndo(vehicleId: number, sessionToken: string, applyBatchId?: string): Promise<any> {
      const response = await this.makeRequest<any>('/modifications_ai_edit_undo.php', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          session_token: sessionToken,
          ...(applyBatchId ? { apply_batch_id: applyBatchId } : {}),
        }),
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to undo edit');
      }
      return response.data || {};
    },

    async aiEditSession(
      vehicleId: number,
      sessionToken: string,
      action: 'status' | 'discard' = 'status'
    ): Promise<any> {
      const response = await this.makeRequest<any>('/modifications_ai_edit_session.php', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: vehicleId,
          session_token: sessionToken,
          action,
        }),
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to load edit session');
      }
      return response.data || {};
    },
} satisfies ThisType<ApiClient>;
